import { useEffect, useRef, useState } from "react";
import { Download, Loader2, Headphones } from "lucide-react";
import { exportConversation } from "@/lib/privacyApi";
import { downloadTranscript } from "@/lib/voice";
import { toast } from "sonner";
import { API } from "@/lib/api";

const FORMATS = [
  { id: "pdf", label: "PDF" },
  { id: "docx", label: "Word (.docx)" },
  { id: "txt", label: "Text (.txt)", local: true },
  { id: "csv", label: "CSV" },
  { id: "json", label: "JSON" },
  { id: "podcast", label: "Podcast (mp3)", audio: true },
];

/**
 * Small dropdown next to a conversation row that triggers a download
 * in the chosen format. Closed by default; opens on click.
 *
 * Pass `anonymous` if the conversation lives only in browser memory —
 * we'll route through the anon export endpoint which never persists.
 */
export default function ExportMenu({ conversation, anonymous = false, testIdPrefix = "export", size = "sm" }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  // Separate flag for the podcast path: it can take 30-60s because
  // the backend loops through every message via TTS + ffmpeg stitch.
  // We need a distinct "Processing…" state so the trigger button
  // reflects that this isn't a snappy click.
  const [busyMode, setBusyMode] = useState(null); // null | "download" | "podcast"
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onClickAway = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    const onEsc = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("mousedown", onClickAway);
    window.addEventListener("keydown", onEsc);
    return () => {
      window.removeEventListener("mousedown", onClickAway);
      window.removeEventListener("keydown", onEsc);
    };
  }, [open]);

  // Fetch the podcast MP3 from the backend and trigger a download. Kept
  // separate from `exportConversation` because the response type is
  // binary audio, not a JSON/PDF/etc. blob wrapped in the export
  // helper. Anonymous conversations aren't supported — audio TTS costs
  // money and we don't want visitor traffic burning through the key.
  const handlePodcast = async () => {
    if (busy) return;
    if (anonymous || !conversation?.id) {
      toast.error("Save the conversation first — anonymous podcasts aren't supported.");
      return;
    }
    setBusy(true);
    setBusyMode("podcast");
    toast.info("Generating podcast — this can take a minute for long chats.");
    try {
      const res = await fetch(
        `${API}/conversations/${encodeURIComponent(conversation.id)}/podcast`,
        { method: "POST", credentials: "include" },
      );
      if (!res.ok) {
        // Backend detail is JSON on 4xx/5xx; try to surface it.
        let msg = `Podcast export failed (${res.status})`;
        try {
          const j = await res.json();
          if (j?.detail) msg = typeof j.detail === "string" ? j.detail : (j.detail.message || msg);
        } catch { /* non-JSON body */ }
        throw new Error(msg);
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const filename =
        res.headers.get("Content-Disposition")?.match(/filename="([^"]+)"/)?.[1]
        || `hbm-podcast-${conversation.id.slice(0, 8)}.mp3`;
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      // Small delay before revoking so Safari finishes the download.
      setTimeout(() => URL.revokeObjectURL(url), 4000);
      toast.success("Podcast ready.");
      setOpen(false);
    } catch (err) {
      toast.error(err.message || "Podcast export failed.");
    } finally {
      setBusy(false);
      setBusyMode(null);
    }
  };

  const handleExport = async (fmt) => {
    if (busy) return;
    if (fmt === "podcast") return handlePodcast();
    // TXT is generated client-side — no backend round trip, no PII in
    // the export pipeline. Same helper Voice Mode uses so the two
    // transcripts read identically.
    if (fmt === "txt") {
      try {
        downloadTranscript(conversation?.messages || [], {
          title: conversation?.title || "conversation",
        });
        toast.success("Downloaded as TXT.");
      } catch (err) {
        toast.error(err.message || "Export failed.");
      } finally {
        setOpen(false);
      }
      return;
    }
    setBusy(true);
    setBusyMode("download");
    try {
      await exportConversation({ conversation, format: fmt, anonymous });
      toast.success(`Downloaded as ${fmt.toUpperCase()}.`);
      setOpen(false);
    } catch (err) {
      toast.error(err.message || "Export failed.");
    } finally {
      setBusy(false);
      setBusyMode(null);
    }
  };

  const iconSize = size === "sm" ? 12 : 14;
  const triggerClass = size === "sm"
    ? "font-mono-ui text-[9px] tracking-[0.24em] uppercase text-[var(--text-dim)] hover:text-[var(--accent)] inline-flex items-center gap-1 transition-colors disabled:opacity-60"
    : "h-9 px-3 rounded-full border border-[var(--border)] bg-[var(--bg-elevated)] hover:border-[var(--accent)] hover:text-[var(--accent)] font-mono-ui text-[10px] tracking-[0.22em] uppercase text-[var(--text)] inline-flex items-center gap-2 transition-colors disabled:opacity-60 shadow-sm";

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          setOpen((v) => !v);
        }}
        disabled={busy}
        className={triggerClass}
        data-testid={`${testIdPrefix}-trigger`}
        aria-label="Export conversation"
        title="Export"
      >
        {busy
          ? <Loader2 size={iconSize} className="animate-spin" />
          : <Download size={iconSize} />}
        {busyMode === "podcast" ? "Processing…" : "Export"}
      </button>
      {open && (
        <div
          role="menu"
          className="absolute right-0 top-full mt-2 z-50 w-56 bg-[var(--bg-elevated)] border border-[var(--border)] shadow-2xl rounded-xl overflow-hidden"
          data-testid={`${testIdPrefix}-menu`}
          onClick={(e) => e.stopPropagation()}
        >
          {FORMATS.map((f) => (
            <button
              key={f.id}
              onClick={() => handleExport(f.id)}
              disabled={busy}
              data-testid={`${testIdPrefix}-${f.id}`}
              className="w-full text-left px-3 py-2 font-mono-ui text-[10px] tracking-[0.24em] uppercase text-[var(--text)] hover:bg-[var(--bg-pill)] hover:text-[var(--accent)] disabled:opacity-40 transition-colors flex items-center justify-between gap-3"
            >
              <span className="inline-flex items-center gap-2">
                {f.audio && <Headphones size={11} />}
                {f.label}
              </span>
              {busy && busyMode === "podcast" && f.id === "podcast" && (
                <Loader2 size={11} className="animate-spin" />
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
